'use client';

import { useState } from 'react';
import useIsomorphicLayoutEffect from './useIsomorphicLayoutEffect';
import {
  DEFAULT_STICKY_NAV_OFFSET_PX,
  isElementDisplayed,
  measureStickyNavOffset,
  readStickyNavOffset,
} from './stickyNavOffset';

export { DEFAULT_STICKY_NAV_OFFSET_PX } from './stickyNavOffset';

export function getDisplayedNavigationHeader(): HTMLElement | null {
  const headerCandidates = document.querySelectorAll<HTMLElement>('#navigation-header');
  for (const headerCandidate of headerCandidates) {
    if (isElementDisplayed(headerCandidate)) {
      return headerCandidate;
    }
  }
  return null;
}

/**
 * Sticky `top` offset for content that should sit below `#navigation-header`.
 * Prefers the `--bz-sticky-nav-offset` value set by the site header;
 * falls back to measuring the displayed navigation header.
 */
export const useStickyNavOffset = (fallbackOffsetPx = DEFAULT_STICKY_NAV_OFFSET_PX): number => {
  const [stickyNavOffset, setStickyNavOffsetState] = useState(fallbackOffsetPx);

  useIsomorphicLayoutEffect(() => {
    let isCancelled = false;
    let pollTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let pollAttempts = 0;
    const maxPollAttempts = 50;

    const updateOffsetIfChanged = (nextOffsetPx: number) => {
      setStickyNavOffsetState(previousOffsetPx =>
        previousOffsetPx === nextOffsetPx ? previousOffsetPx : nextOffsetPx,
      );
    };

    const tryResolveStickyNavOffset = (): boolean => {
      if (isCancelled) return true;
      const offsetFromCssVar = readStickyNavOffset();
      if (offsetFromCssVar !== null) {
        updateOffsetIfChanged(offsetFromCssVar);
        return true;
      }
      const headerElement = getDisplayedNavigationHeader();
      if (!headerElement) return false;
      updateOffsetIfChanged(measureStickyNavOffset(headerElement));
      return true;
    };

    const pollUntilOffsetResolved = () => {
      if (isCancelled) return;
      if (tryResolveStickyNavOffset() || pollAttempts >= maxPollAttempts) return;
      pollAttempts += 1;
      pollTimeoutId = setTimeout(pollUntilOffsetResolved, 100);
    };

    pollUntilOffsetResolved();

    const cssVarObserver = new MutationObserver(tryResolveStickyNavOffset);
    cssVarObserver.observe(document.documentElement, { attributeFilter: ['style'], attributes: true });

    return () => {
      isCancelled = true;
      if (pollTimeoutId) clearTimeout(pollTimeoutId);
      cssVarObserver.disconnect();
    };
  }, []);

  return stickyNavOffset;
};

export default useStickyNavOffset;
